<template>
  <v-card flat class="preview-root">
    <v-card-text class="preview-content">
      <v-toolbar flat class="panel-surface toolbar-surface mb-4">
        <v-toolbar-title>Vista previa de partida contable</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          icon
          :loading="loadingTemplates"
          @click="loadTemplates"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card outlined class="panel-surface section-card mb-4">
        <v-card-text class="pa-5">
          <v-row class="mt-0">
        <v-col cols="12" md="5">
          <v-select
            v-model="selectedTemplate"
            :items="templates"
            item-text="Name"
            item-value="ID"
            label="Seleccione una plantilla"
            outlined
            dense
            return-object
            :loading="loadingTemplates"
            class="panel-surface"
            @change="onTemplateChange"
          >
            <template v-slot:item="{ item }">
              <div class="d-flex flex-column py-1">
                <span>{{ item.Name || `Plantilla ${ item.PlanNumber }` }}</span>
                <small class="grey--text">
                  Plan {{ item.PlanNumber }} | Envío {{ formatSendNumber(item.SendNumber) }} | {{ formatDate(item.Date) }}
                </small>
              </div>
            </template>
            <template v-slot:selection="{ item }">
              <span>{{ item.Name || `Plantilla ${ item.PlanNumber }` }}</span>
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" md="4">
          <v-select
            v-model="selectedAccountingMode"
            :items="accountingModeOptions"
            item-text="text"
            item-value="value"
            label="Modo de partida contable"
            outlined
            dense
            class="panel-surface"
            @change="onModeChange"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-btn
            color="primary"
            block
            :disabled="!selectedTemplateId || selectedTemplateStatusPayment != 1"
            :loading="loadingPreview"
            @click="loadPreview"
          >
            <v-icon left>mdi-eye</v-icon>
            Ver vista previa
          </v-btn>
        </v-col>
          </v-row>
          <v-alert
            v-if="selectedTemplateId && selectedTemplateStatusPayment != 1"
            dense
            type="info"
            outlined
            class="mt-3 mb-0"
          >
            La plantilla debe estar procesada antes de generar su partida contable.
          </v-alert>
        </v-card-text>
      </v-card>

      <v-card
        v-if="preview && hasBlockingConfigurationIssues"
        outlined
        class="mt-4 panel-surface section-card"
      >
        <v-card-text class="pa-5">
          <div class="text-subtitle-1 font-weight-bold mb-4">Requisitos contables pendientes</div>

          <v-alert
            v-if="preview.validation?.missingBankAccount"
            type="warning"
            outlined
            class="mb-4"
          >
            <div class="d-flex justify-space-between align-center flex-wrap">
              <span>Debe configurar la cuenta contable de banco antes de continuar.</span>
              <v-btn color="primary" small class="mt-2 mt-md-0" @click="openBankAccountDialog">
                <v-icon left small>mdi-bank-plus</v-icon>
                Configurar banco
              </v-btn>
            </div>
          </v-alert>

          <div v-if="missingProviders.length">
            <div class="text-body-1 font-weight-medium mb-3">
              Proveedores sin cuenta de cuentas por pagar individual
            </div>
            <v-simple-table dense class="table-surface">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Proveedor</th>
                    <th>Documento</th>
                    <th class="text-right">Accion</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="provider in missingProviders" :key="`${provider.id_provider}-${provider.provider_name}`">
                    <td>{{ provider.provider_name }}</td>
                    <td>{{ provider.document_number || '-' }}</td>
                    <td class="text-right">
                      <v-btn small color="primary" outlined @click="openProviderAccountDialog(provider)">
                        <v-icon left small>mdi-account-cash</v-icon>
                        Configurar cuenta
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </v-card-text>
      </v-card>

      <v-alert
        v-if="warnings.length && !hasBlockingConfigurationIssues"
        type="warning"
        outlined
        class="mt-3"
      >
        <div v-for="(warning, index) in warnings" :key="`warning-${index}`">
          {{ warning }}
        </div>
      </v-alert>

      <v-alert
        v-if="preview && !hasBlockingConfigurationIssues && !hasAccountingDocuments"
        type="info"
        outlined
        class="mt-4"
      >
        No hay documentos validos para generar la partida contable con los filtros y estados actuales.
      </v-alert>

      <template v-if="preview && !hasBlockingConfigurationIssues && hasAccountingDocuments">
        <v-card outlined class="mt-4 panel-surface">
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" md="3">
                <div class="text-subtitle-2 grey--text">Plantilla</div>
                <div class="text-h6">{{ preview.template.Name || `Plan ${preview.template.PlanNumber}` }}</div>
              </v-col>
              <v-col cols="12" md="3">
                <div class="text-subtitle-2 grey--text">Documentos</div>
                <div class="text-h6">{{ preview.summary.totalDocuments }}</div>
              </v-col>
              <v-col cols="12" md="3">
                <div class="text-subtitle-2 grey--text">Modo de partida</div>
                <div class="text-h6">{{ accountingModeLabel }}</div>
              </v-col>
            </v-row>
            <v-divider class="my-4"></v-divider>
            <v-row class="pt-3">
              <v-col cols="12" md="3" class="pb-3">
                <v-menu
                  v-model="menuAccountingEntryDate"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="accountingEntryDate"
                      label="Fecha de la partida"
                      append-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="accountingEntryDate"
                    locale="es"
                    @input="menuAccountingEntryDate = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="5" class="pb-3">
                <v-text-field
                  v-model="accountingEntryConcept"
                  label="Concepto de la partida"
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4" class="pb-3">
                <v-select
                  v-model="selectedAccountingEntryTypeId"
                  :items="accountingEntryTypes"
                  item-text="accounting_entry_name"
                  item-value="id_accounting_entry_type"
                  label="Tipo de partida"
                  outlined
                  dense
                  hide-details
                  :loading="loadingAccountingEntryTypes"
                ></v-select>
              </v-col>
            </v-row>
            <v-chip
              class="mt-2"
              :color="preview.summary.isBalanced ? 'success' : 'error'"
              outlined
            >
              <v-icon left small>
                {{ preview.summary.isBalanced ? 'mdi-check-circle' : 'mdi-alert-circle' }}
              </v-icon>
              {{ preview.summary.isBalanced ? 'La partida está balanceada' : 'La partida no está balanceada' }}
            </v-chip>
            <div class="mt-3 d-flex justify-end align-center">
              <v-btn
                v-if="selectedTemplate && selectedTemplate.ID_Accounting_item"
                small
                color="success"
                outlined
                class="mr-2"
                @click="openAccountingEntryDetail"
              >
                <v-icon left small>mdi-book-check</v-icon>
                Partida: {{ selectedTemplate.ID_Accounting_item }}
              </v-btn>
              <v-btn
                v-else
                color="primary"
                :loading="generatingAccountingEntry"
                :disabled="!preview.summary.isBalanced || !selectedTemplateId || hasBlockingConfigurationIssues || !hasAccountingDocuments || !selectedAccountingEntryTypeId || loadingAccountingEntryTypes"
                @click="generateAccountingEntry"
              >
                <v-icon left>mdi-book-plus</v-icon>
                Generar partida contable
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <v-card outlined class="mt-4 panel-surface">
          <v-card-title class="subtitle-1 font-weight-bold">
            Partida resumida por cuenta
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="lineHeaders"
              :items="previewLinesWithTotals"
              dense
              hide-default-footer
              class="table-surface"
              :item-class="getLineRowClass"
            >
              <template v-slot:[`item.debit`]="{ item }">
                ${{ Number(item.debit || 0).toFixed(2) }}
              </template>
              <template v-slot:[`item.credit`]="{ item }">
                ${{ Number(item.credit || 0).toFixed(2) }}
              </template>
              <template v-slot:no-data>
                <v-alert type="info" outlined class="ma-3">
                  No hay líneas contables disponibles para mostrar.
                </v-alert>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <v-card outlined class="mt-4 panel-surface">
          <v-card-title class="subtitle-1 font-weight-bold">
            Información por documento
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="documentHeaders"
              :items="preview.supportingDocuments"
              dense
              :items-per-page="10"
              class="elevation-0 table-surface"
            >
              <template v-slot:[`item.amount`]="{ item }">
                ${{ Number(item.amount || 0).toFixed(2) }}
              </template>
              <template v-slot:[`item.originType`]="{ item }">
                {{ formatOriginType(item.originType) }}
              </template>
              <template v-slot:[`item.invoiceStatus`]="{ item }">
                <v-chip
                  x-small
                  outlined
                  :color="getInvoiceStatusColor(item.invoiceStatus)"
                >
                  {{ formatInvoiceStatus(item.invoiceStatus) }}
                </v-chip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </template>

      <v-alert
        v-if="!loadingPreview && !preview && !warnings.length"
        type="info"
        outlined
        class="mt-4"
      >
        Seleccione una plantilla para visualizar la partida contable propuesta.
      </v-alert>
    </v-card-text>

    <alerts
      v-if="alert.show"
      v-on:close_alert="closeAlert"
      v-on:accept_alert="acceptAlert"
      :properties="alert"
    ></alerts>

    <v-dialog
      v-model="showBankAccountDialog"
      max-width="1150"
      persistent
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Seleccionar cuenta de banco</span>
          <v-btn icon @click="closeBankAccountDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-0">
          <AccountingItemForm
            :isAllowTransaction="false"
            @sendSelection="onBankAccountSelected"
            @closeDialog="closeBankAccountDialog"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showProviderAccountDialog"
      max-width="1150"
      persistent
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Seleccionar cuenta por pagar del proveedor</span>
          <v-btn icon @click="closeProviderAccountDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-0">
          <AccountingItemForm
            :isAllowTransaction="false"
            @sendSelection="onProviderAccountSelected"
            @closeDialog="closeProviderAccountDialog"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import alerts from '@/components/Alerts.vue';
import AccountingItemForm from '@/views/acounting/partial/AccountingItemForm.vue';

export default {
  name: 'PaymentTemplateAccountingPreview',
  components: {
    alerts,
    AccountingItemForm
  },
  props: {
    initialTemplateId: {
      type: [Number, String],
      default: null
    },
    initialMode: {
      type: String,
      default: 'general'
    },
    activationToken: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      loadingTemplates: false,
      loadingPreview: false,
      generatingAccountingEntry: false,
      loadingAccountingEntryTypes: false,
      menuAccountingEntryDate: false,
      showBankAccountDialog: false,
      showProviderAccountDialog: false,
      selectedProviderForConfig: null,
      accountingEntryDate: null,
      accountingEntryConcept: '',
      accountingEntryTypes: [],
      selectedAccountingEntryTypeId: null,
      templates: [],
      selectedTemplate: null,
      selectedAccountingMode: 'general',
      preview: null,
      warnings: [],
      accountingModeOptions: [
        { text: 'Por cuenta general', value: 'general' },
        { text: 'Por cuenta de proveedores individual', value: 'provider' }
      ],
      lineHeaders: [
        { text: 'Código', value: 'account_code' },
        { text: 'Cuenta', value: 'account_description' },
        { text: 'Concepto', value: 'concept' },
        //{ text: 'Movimiento', value: 'movementType' },
        { text: 'Debe', value: 'debit', align: 'end' },
        { text: 'Haber', value: 'credit', align: 'end' }
      ],
      documentHeaders: [
        { text: 'Origen', value: 'originType' },
        { text: 'Documento', value: 'documentNumber' },
        { text: 'Proveedor', value: 'providerName' },
        { text: 'Estado Factura', value: 'invoiceStatus' },
        { text: 'Monto', value: 'amount', align: 'end' },
        { text: 'Cuenta contable', value: 'debit_account_code' },
        { text: 'Descripción', value: 'debit_account_description' }
      ],
      alert: {
        type: 'success',
        show: false,
        header: '',
        body: ''
      }
    };
  },
  mounted() {
    this.loadTemplates();
    this.loadAccountingEntryTypes();
  },
  watch: {
    activationToken() {
      this.applyInitialSelection();
    }
  },
  computed: {
    selectedTemplateId() {
      return this.selectedTemplate?.ID || null;
    },
    selectedTemplateStatusPayment() {
      return Number(this.selectedTemplate?.StatusPayment || 0);
    },
    accountingModeLabel() {
      return this.selectedAccountingMode === 'provider'
        ? 'Por cuenta de proveedores individual'
        : 'Por cuenta general';
    },
    missingProviders() {
      return this.preview?.validation?.missingProviders || [];
    },
    hasBlockingConfigurationIssues() {
      if (!this.preview?.validation) {
        return false;
      }

      return Boolean(
        this.preview.validation.missingBankAccount ||
        (this.preview.validation.missingProviders || []).length
      );
    },
    hasAccountingDocuments() {
      return Number(this.preview?.summary?.totalDocuments || 0) > 0;
    },
    previewLinesWithTotals() {
      if (!this.preview?.lines?.length) {
        return [];
      }

      const totalDebit = Number(this.preview.summary?.totalDebit || 0);
      const totalCredit = Number(this.preview.summary?.totalCredit || 0);
      const difference = Math.abs(totalDebit - totalCredit);

      return [
        ...this.preview.lines,
        {
          account_code: '',
          account_description: '',
          detail: 'Totales',
          concept: `Diferencia: $${difference.toFixed(2)}`,
          debit: totalDebit,
          credit: totalCredit,
          isTotalsRow: true
        }
      ];
    }
  },
  methods: {
    closeAlert() {
      this.alert.show = false;
    },
    acceptAlert() {
      this.alert.show = false;
    },
    showAlert(type, header, body, options = null) {
      type = type == null ? 'danger' : type;
      this.alert.type = type;
      this.alert.header = header;
      this.alert.body = body;
      this.alert.show = true;
      this.alert.options = options != null ? options : null;
    },
    formatSendNumber(sendNumber) {
      if (!sendNumber && sendNumber !== 0) return 'N/A';
      return String(sendNumber).padStart(5, '0');
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('es-ES');
    },
    formatOriginType(originType) {
      const normalized = String(originType || '').trim().toLowerCase();

      if (normalized === 'purchase') {
        return 'Compra';
      }

      if (normalized === 'expense') {
        return 'Gasto';
      }

      if (normalized === 'unknown') {
        return 'Sin clasificar';
      }

      return originType || 'Sin clasificar';
    },
    formatInvoiceStatus(status) {
      if (status === null || status === undefined || status === '') {
        return 'Sin estado';
      }

      const normalized = String(status).trim().toUpperCase();
      const statusMap = {
        CANCELADA: 'Cancelada',
        CANCELADO: 'Cancelado',
        CREDITO: 'Crédito',
        'CRÉDITO': 'Crédito',
        ACREDITADO: 'Acreditado',
        ABONO: 'Abono',
        INVALIDADO: 'Invalidado',
        EMITIDO: 'Emitido',
        CONTADO: 'Contado',
        ANTICIPO: 'Anticipo',
        'NOTA DE CREDITO': 'Nota de crédito',
        'NOTA DE CRÉDITO': 'Nota de crédito',
        'NOTA DE DEBITO': 'Nota de débito',
        'NOTA DE DÉBITO': 'Nota de débito'
      };

      return statusMap[normalized] || String(status);
    },
    getInvoiceStatusColor(status) {
      const normalized = String(status || '').trim().toUpperCase();

      if (['CANCELADA', 'CANCELADO', 'CONTADO', 'EMITIDO'].includes(normalized)) {
        return 'success';
      }

      if (['CREDITO', 'CRÉDITO', 'ACREDITADO', 'ABONO', 'ANTICIPO'].includes(normalized)) {
        return 'primary';
      }

      if (['INVALIDADO', 'NOTA DE CREDITO', 'NOTA DE CRÉDITO', 'NOTA DE DEBITO', 'NOTA DE DÉBITO'].includes(normalized)) {
        return 'error';
      }

      return 'grey';
    },
    async loadTemplates() {
      this.loadingTemplates = true;
      try {
        const response = await this.$API.paymenttemplate.getAllTemplates();
        this.templates = response || [];
        this.applyInitialSelection();
      } catch (error) {
        console.error('Error al cargar plantillas para vista previa contable:', error);
        this.showAlert('danger', 'Error', 'No se pudieron cargar las plantillas de pago.');
        this.templates = [];
      } finally {
        this.loadingTemplates = false;
      }
    },
    async loadAccountingEntryTypes() {
      this.loadingAccountingEntryTypes = true;
      try {
        const response = await this.$API.branchaccountcatalog.getAcounteringType();
        this.accountingEntryTypes = response?.data || [];

        if (!this.selectedAccountingEntryTypeId) {
          const defaultType = this.accountingEntryTypes.find(item => {
            const code = String(item.accounting_entry_code || '').trim().toUpperCase();
            const name = String(item.accounting_entry_name || '').trim().toUpperCase();
            return code === 'ABONO' || name === 'ABONO';
          }) || this.accountingEntryTypes[0];

          this.selectedAccountingEntryTypeId = defaultType?.id_accounting_entry_type || null;
        }
      } catch (error) {
        console.error('Error al cargar tipos de partida contable:', error);
        this.accountingEntryTypes = [];
        this.selectedAccountingEntryTypeId = null;
      } finally {
        this.loadingAccountingEntryTypes = false;
      }
    },
    onTemplateChange(template) {
      this.preview = null;
      this.warnings = [];
      this.selectedTemplate = template || null;
      this.loadPreview();
    },
    onModeChange() {
      this.preview = null;
      this.warnings = [];
      this.loadPreview();
    },
    syncAccountingEntryFields() {
      if (!this.preview?.template) {
        return;
      }

      this.accountingEntryDate = this.preview.template.Date || null;
      this.accountingEntryConcept = this.preview.template.Name
        ? `Plantilla de pago ${this.preview.template.PlanNumber} - ${this.preview.template.Name}`
        : `Plantilla de pago ${this.preview.template.PlanNumber}`;
    },
    openBankAccountDialog() {
      this.showBankAccountDialog = true;
    },
    closeBankAccountDialog() {
      this.showBankAccountDialog = false;
    },
    openProviderAccountDialog(provider) {
      this.selectedProviderForConfig = provider;
      this.showProviderAccountDialog = true;
    },
    closeProviderAccountDialog() {
      this.showProviderAccountDialog = false;
      this.selectedProviderForConfig = null;
    },
    async onBankAccountSelected(account) {
      try {
        const response = await this.$API.branchaccountconfig.updateBankAccountConfig({
          bank_account_id_account: account.id_account
        });

        if (!response?.success) {
          throw new Error(response?.error || 'No fue posible guardar la cuenta de banco');
        }

        this.closeBankAccountDialog();
        this.showAlert('success', 'Exito', 'Cuenta de banco configurada correctamente.');
        await this.loadPreview();
      } catch (error) {
        console.error('Error al guardar cuenta de banco:', error);
        this.showAlert('danger', 'Error', 'No se pudo guardar la cuenta de banco seleccionada.');
      }
    },
    async onProviderAccountSelected(account) {
      if (!this.selectedProviderForConfig?.id_provider) {
        this.showAlert('warning', 'Advertencia', 'No se pudo identificar el proveedor a configurar.');
        return;
      }

      try {
        const response = await this.$API.accountingexpenses.updateProviderAccounts({
          ID_Provider: this.selectedProviderForConfig.id_provider,
          CuentaCuentasPorPagar: account.id_account
        });

        if (!(response?.success || response?.data)) {
          throw new Error('No fue posible actualizar la cuenta del proveedor');
        }

        const providerName = this.selectedProviderForConfig.provider_name;
        this.closeProviderAccountDialog();
        this.showAlert('success', 'Exito', `Cuenta por pagar configurada para ${providerName}.`);
        await this.loadPreview();
      } catch (error) {
        console.error('Error al guardar cuenta del proveedor:', error);
        this.showAlert('danger', 'Error', 'No se pudo guardar la cuenta del proveedor seleccionada.');
      }
    },
    getLineRowClass(item) {
      return item?.isTotalsRow ? 'preview-totals-row' : '';
    },
    applyInitialSelection() {
      if (!this.initialTemplateId || !this.templates.length) {
        return;
      }

      const matchedTemplate = this.templates.find(
        template => String(template.ID) === String(this.initialTemplateId)
      );

      if (!matchedTemplate) {
        return;
      }

      this.selectedTemplate = matchedTemplate;
      this.selectedAccountingMode = this.initialMode || 'general';
      this.preview = null;
      this.warnings = [];
      this.loadPreview();
    },
    async loadPreview() {
      if (!this.selectedTemplateId) {
        this.showAlert('warning', 'Advertencia', 'Seleccione una plantilla para generar la vista previa.');
        return;
      }

      if (this.selectedTemplateStatusPayment != 1) {
        this.preview = null;
        this.warnings = [];
        this.showAlert('warning', 'Advertencia', 'La plantilla debe estar procesada antes de generar su partida contable.');
        return;
      }

      this.loadingPreview = true;
      try {
        const response = await this.$API.paymenttemplate.getAccountingPreview(this.selectedTemplateId, this.selectedAccountingMode);
        this.preview = response.data;
        this.warnings = response.data?.warnings || [];
        this.syncAccountingEntryFields();
      } catch (error) {
        console.error('Error al generar vista previa contable:', error);
        this.preview = null;
        this.warnings = [];
        const message = error?.response?.data?.message || 'No se pudo generar la vista previa contable.';
        this.showAlert('danger', 'Error', message);
      } finally {
        this.loadingPreview = false;
      }
    },
    async generateAccountingEntry() {
      if (!this.selectedTemplateId) {
        this.showAlert('warning', 'Advertencia', 'Seleccione una plantilla valida.');
        return;
      }

      if (!this.selectedAccountingEntryTypeId) {
        this.showAlert('warning', 'Advertencia', 'Seleccione el tipo de partida contable.');
        return;
      }

      this.generatingAccountingEntry = true;
      try {
        const response = await this.$API.paymenttemplate.createAccountingEntryFromTemplate(
          this.selectedTemplateId,
          this.selectedAccountingMode,
          {
            id_accounting_entry_type: this.selectedAccountingEntryTypeId,
            accounting_entry_date: this.accountingEntryDate,
            accounting_entry_comment: this.accountingEntryConcept
          }
        );

        this.selectedTemplate = {
          ...this.selectedTemplate,
          id_accounting_entry: response.data.id_accounting_entry,
          ID_Accounting_item: response.data.ID_Accounting_item,
          accounting_entry_mode: response.data.accounting_entry_mode
        };

        const templateIndex = this.templates.findIndex(item => String(item.ID) === String(this.selectedTemplateId));
        if (templateIndex >= 0) {
          this.templates.splice(templateIndex, 1, {
            ...this.templates[templateIndex],
            id_accounting_entry: response.data.id_accounting_entry,
            ID_Accounting_item: response.data.ID_Accounting_item,
            accounting_entry_mode: response.data.accounting_entry_mode
          });
        }

        if (this.preview?.template) {
          this.preview = {
            ...this.preview,
            template: {
              ...this.preview.template,
              id_accounting_entry: response.data.id_accounting_entry,
              ID_Accounting_item: response.data.ID_Accounting_item,
              accounting_entry_mode: response.data.accounting_entry_mode
            }
          };
        }

        this.showAlert(
          'success',
          'Exito',
          `Partida contable generada correctamente: ${response.data.ID_Accounting_item}`
        );
      } catch (error) {
        console.error('Error al generar partida contable:', error);
        const message = error?.response?.data?.message || 'No se pudo generar la partida contable.';
        this.showAlert('danger', 'Error', message);
      } finally {
        this.generatingAccountingEntry = false;
      }
    },
    openAccountingEntryDetail() {
      if (!this.selectedTemplate?.id_accounting_entry) {
        this.showAlert('warning', 'Advertencia', 'La plantilla no tiene una partida contable asociada.');
        return;
      }

      this.$store.dispatch('addWindow', {
        name: 'Acounting',
        component: 'Acounting',
        unique: false,
        meta: {
          accountingEntryToModify: {
            id_accounting_entry: this.selectedTemplate.id_accounting_entry,
            ID_Accounting_item: this.selectedTemplate.ID_Accounting_item
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.preview-root {
  background: #fff;
}

.preview-content {
  background: #fff;
  padding: 24px !important;
}

.panel-surface {
  background: #fff !important;
}

.toolbar-surface {
  border: 1px solid #e0e0e0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.section-card {
  border-color: #dcdcdc !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

::v-deep .table-surface {
  background: #fff !important;
}

::v-deep .table-surface .v-data-table__wrapper,
::v-deep .table-surface table,
::v-deep .table-surface thead,
::v-deep .table-surface tbody,
::v-deep .table-surface tr,
::v-deep .table-surface th,
::v-deep .table-surface td {
  background: #fff !important;
}

::v-deep .panel-surface .v-input__slot {
  background: #fff !important;
}

::v-deep .section-card .v-data-table {
  border: 1px solid #ececec;
  border-radius: 4px;
}

::v-deep .preview-totals-row td {
  font-weight: 700;
  border-top: 2px solid #d9d9d9;
}
</style>
